import { queryOptions, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { toast } from "@patient-watch/ui/sonner";
import { supabaseClient } from "@/lib/supabase";
import type { ProcessedScheduleT, ScheduleViewT } from "@patient-watch/types";
import { selectScheduleById } from "./selectSchedulesById";
export async function processSchedule(schedule: ScheduleViewT, referenceDate: string) {
    if (!schedule?.schedule_id || !schedule.timings?.length) {
        return [];
    }

    const { data, error } = await supabaseClient.functions.invoke("public-process-schedule", {
        body: { schedule, referenceDate },
    });

    if (error) {
        const errorMessage = `Error processing schedule. ${error.message}`;
        console.error(errorMessage);
        toast.error(errorMessage);
        return [];
    }

    return data as ProcessedScheduleT[];
}

export const processScheduleQueryOptions = (schedule: ScheduleViewT, referenceDate: string) => {
    return queryOptions({
        queryKey: ["process-schedule", schedule.schedule_id, referenceDate],
        queryFn: () => processSchedule(schedule, referenceDate),
    });
};

export const processScheduleFromNowQueryOptions = (schedule: ScheduleViewT) => {
    const now = dayjs().toISOString();
    return queryOptions({
        queryKey: ["process-schedule", schedule.schedule_id, now],
        queryFn: () => processSchedule(schedule, now),
        enabled: !!schedule,
    });
};

export const useProcessScheduleQuery = (schedule: ScheduleViewT, referenceDate: string) => {
    return useQuery(processScheduleQueryOptions(schedule, referenceDate));
};

export const useProcessScheduleFromNowQuery = (schedule: ScheduleViewT) => {
    return useQuery(processScheduleFromNowQueryOptions(schedule));
};

export const fetchAndProcessSchedule = async (scheduleId: string, referenceDate: string) => {
    const schedule = await selectScheduleById(scheduleId);
    if (!schedule) throw new Error("Schedule not found");
    return processSchedule(schedule, referenceDate);
};

const processedScheduleByIdQueryOptions = (scheduleId: string) =>
    queryOptions({
        queryKey: ["schedule", "processed", scheduleId],
        queryFn: () => fetchAndProcessSchedule(scheduleId, dayjs().toISOString()),
        enabled: !!scheduleId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

export const useSelectAndProcessSchedule = (scheduleId: string) => {
    return useSuspenseQuery(processedScheduleByIdQueryOptions(scheduleId));
};

/** Non-suspense version, for pages that render their own loading state. */
export const useProcessedScheduleQuery = (scheduleId: string) => {
    return useQuery(processedScheduleByIdQueryOptions(scheduleId));
};
